import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
// import { Link } from 'react-router-dom'; // 웹빌더에서는 라우터 미사용
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';

import { SideBar } from './SideBar';
import { MenuIcon } from '../icons';
import { GNB_MOCK_DATA } from '../../constants/navigation';

import type { GNBData } from '../../types/navigation';

gsap.registerPlugin(useGSAP);

interface GnbProps {
    menus?: any[];
    isLoggedIn?: boolean;
    isBusiness?: boolean;
}

/**
 * Gnb 컴포넌트 - 글로벌 네비게이션 바 컴포넌트
 *
 * 구조:
 * - 데스크톱: 호버 기반 메가메뉴 시스템
 * - 모바일: 햄버거 메뉴 버튼으로 사이드바 토글
 *
 * 상태 관리:
 * - isSideBarOpen: 사이드바 열림/닫힘 상태
 * - 데스크톱 메가메뉴: hoveredItem, isMegaMenuOpen, megaMenuTimeline
 *
 * 애니메이션:
 * - GSAP Timeline을 활용한 메가메뉴 fade/slide 애니메이션
 * - 호버 상호작용과 포커스 관리
 *
 * Props:
 * - SideBar: isOpen, onClose 함수 전달
 * - MobileGnb: onOpen 함수 전달
 */
export function Gnb({ menus = [], isLoggedIn = false, isBusiness = false }: GnbProps) {
    const [isSideBarOpen, setIsSideBarOpen] = useState<boolean>(false);

    function handleOpenSideBar() {
        setIsSideBarOpen(true);
        document.body.style.overflow = 'hidden'; // 스크롤 방지
    }

    function handleCloseSideBar() {
        setIsSideBarOpen(false);
        document.body.style.overflow = ''; // 스크롤 복원
    }

    return (
        <nav
            id="gnb"
            className="lg:w-[300px]"
        >
            <DesktopGnb menus={menus} isLoggedIn={isLoggedIn} isBusiness={isBusiness} />
            <MobileGnb onOpen={handleOpenSideBar} />
            <SideBar
                isOpen={isSideBarOpen}
                onClose={handleCloseSideBar}
                menus={menus}
                isLoggedIn={isLoggedIn}
                isBusiness={isBusiness}
            />
        </nav>
    );
}

interface DesktopGnbProps {
    menus: any[];
    isLoggedIn?: boolean;
    isBusiness?: boolean;
}

function DesktopGnb({ menus, isLoggedIn = false, isBusiness = false }: DesktopGnbProps) {
    const [hoveredItem, setHoveredItem] = useState<GNBData | null>(null);
    const [isMegaMenuOpen, setIsMegaMenuOpen] = useState<boolean>(false);
    const [isMounted, setIsMounted] = useState<boolean>(false);
    const [isMouseInHeader, setIsMouseInHeader] = useState<boolean>(false);
    const [isMouseInMegaMenu, setIsMouseInMegaMenu] = useState<boolean>(false);

    function handleMouseHover(item: GNBData) {
        if (item.children && item.children.length > 0) {
            setHoveredItem(item);
            setIsMegaMenuOpen(true);
        } else {
            // depth가 없는 메뉴를 hover하면 메가 메뉴 닫기
            setHoveredItem(null);
            setIsMegaMenuOpen(false);
        }
    }

    function handleGnbMouseLeave() {
        setIsMouseInHeader(false);
        // 마우스가 헤더와 메가메뉴 모두에서 벗어났을 때만 닫기
        setTimeout(() => {
            if (!isMouseInHeader && !isMouseInMegaMenu) {
                setIsMegaMenuOpen(false);
            }
        }, 100);
    }

    function handleGnbMouseEnter() {
        setIsMouseInHeader(true);
    }

    function handleMegaMenuMouseLeave() {
        setIsMouseInMegaMenu(false);
        // 마우스가 헤더와 메가메뉴 모두에서 벗어났을 때만 닫기
        setTimeout(() => {
            if (!isMouseInHeader && !isMouseInMegaMenu) {
                setIsMegaMenuOpen(false);
            }
        }, 100);
    }

    function handleMegaMenuMouseEnter() {
        setIsMouseInMegaMenu(true);
        // 메가 메뉴에 마우스가 들어오면 상태 유지
        if (hoveredItem) {
            setIsMegaMenuOpen(true);
        }
    }

    // 컨포넌트 마운트되면 상태를 변경
    useEffect(() => {
        setIsMounted(true);
    }, []);

    useGSAP(() => {
        // 브레이크 포인트 fade-in 애니메이션
        const gnb = document.querySelector('.desktopGnb');
        if (gnb) {
            // GSAP 존재 여부 체크
            if (typeof gsap !== 'undefined') {
                // matchMedia 대신 직접 윈도우 크기 확인
                const checkAndAnimate = () => {
                    if (window.innerWidth >= 1024) { // 64rem = 1024px
                        gsap.to(gnb, {
                            opacity: 1,
                            duration: 0.9,
                            ease: 'power2.out',
                        });
                    }
                };
                
                // 초기 실행
                checkAndAnimate();
                
                // 리사이즈 이벤트에도 대응
                window.addEventListener('resize', checkAndAnimate);
                
                return () => {
                    window.removeEventListener('resize', checkAndAnimate);
                };
            } else {
                // GSAP이 없을 때는 직접 opacity 설정
                if (window.innerWidth >= 1024) {
                    (gnb as HTMLElement).style.opacity = '1';
                }
            }
        }
    }, []);

    // 메가메뉴 닫기 시 hoveredItem 초기화
    useEffect(() => {
        if (!isMegaMenuOpen) {
            const timer = setTimeout(() => {
                setHoveredItem(null);
            }, 300); // transition 시간과 동일
            return () => clearTimeout(timer);
        }
    }, [isMegaMenuOpen]);
    
    // 마우스가 헤더와 메가메뉴 모두에서 벗어났는지 체크
    useEffect(() => {
        if (!isMouseInHeader && !isMouseInMegaMenu) {
            const timer = setTimeout(() => {
                setIsMegaMenuOpen(false);
                setHoveredItem(null);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isMouseInHeader, isMouseInMegaMenu]);

    return (
        <>
            <DesktopGnbNavigation
                hoveredItem={hoveredItem}
                handleMouseHover={handleMouseHover}
                handleGnbMouseLeave={handleGnbMouseLeave}
                handleGnbMouseEnter={handleGnbMouseEnter}
                menus={menus}
                isLoggedIn={isLoggedIn}
                isBusiness={isBusiness}
            />
            {/* DOM이 마운트되면 Portal 렌더링 */}
            {/* 헤더에서 props drilling을 피하기 위해 portal 사용 */}
            {isMounted &&
                createPortal(
                    <DesktopGnbMegaMenu
                        hoveredItem={hoveredItem}
                        handleMegaMenuMouseEnter={handleMegaMenuMouseEnter}
                        handleMegaMenuMouseLeave={handleMegaMenuMouseLeave}
                        isOpen={isMegaMenuOpen}
                    />,
                    document.querySelector('#megaMenuContainer') || document.querySelector('#header') || document.body
                )}
        </>
    );
}

// 데스크톱 GNB 네비게이션 컴포넌트
function DesktopGnbNavigation({ hoveredItem, handleMouseHover, handleGnbMouseLeave, handleGnbMouseEnter, menus, isLoggedIn, isBusiness }: { hoveredItem: GNBData | null; handleMouseHover: (item: GNBData) => void; handleGnbMouseLeave: () => void; handleGnbMouseEnter: () => void; menus: any[]; isLoggedIn: boolean; isBusiness: boolean }) {

    
    // URL 처리 함수
    const getUrl = (menuItem: any) => {
        // 외부 링크 처리
        if (menuItem.externalLink) {
            return menuItem.externalLink;
        }
        // path를 우선적으로 사용 (flone과 동일)
        if (menuItem.path) {
            return menuItem.path;
        }
        if (menuItem.url) {
            return menuItem.url;
        }
        if (menuItem.link) {
            return menuItem.link;
        }
        if (menuItem.menu_srl) {
            return `/board/${menuItem.menu_srl}`;
        }
        return '#';
    };

    // 웹빌더 메뉴 데이터를 GNBData 형식으로 변환
    const navigationData = menus.length > 0 ? (() => {
        // 메뉴 정렬 (order 기준)
        const sortedMenus = [...menus].sort((a, b) => (a.order || 0) - (b.order || 0));
        
        // 1단계: parentId가 null인 루트 메뉴 찾기 + 필터링 적용
        const rootMenus = sortedMenus.filter(menu => {
            // parentId가 있는 메뉴는 하위 메뉴이므로 제외
            if (menu.parentId !== null) {
                return false;
            }
            
            // 글로벌 메뉴는 제외
            if (menu.type === 'GLOBAL') {
                return false;
            }
            
            // visible이 false인 메뉴 제외
            if (menu.visible === false) {
                return false;
            }
            
            // 사업자 전용 메뉴 필터링
            if (menu.is_business_only && (!isLoggedIn || !isBusiness)) {
                return false;
            }
            
            // 로그인 상태별 메뉴 필터링
            if (!menu.is_business_only) {
                // 로그인 관련 속성이 없는 경우 모두 표시
                if (!menu.is_logged && !menu.is_not_logged) {
                    return true;
                }
                // 로그인 필요 메뉴는 로그인 시만 표시
                if (menu.is_logged && !isLoggedIn) {
                    return false;
                }
                // 비로그인 메뉴는 로그아웃 시만 표시
                if (menu.is_not_logged && isLoggedIn) {
                    return false;
                }
            }
            
            return true;
        });
        
        // 2단계: 각 루트 메뉴에 대해 계층 구조 구축
        return rootMenus.map(rootMenu => {
            // 현재 메뉴의 자식 찾기 (로그인 상태 필터링 적용)
            const children = sortedMenus.filter(menu => {
                if (menu.parentId !== rootMenu.id) return false;
                if (menu.visible === false) return false;
                
                // 사업자 전용 메뉴 필터링
                if (menu.is_business_only && (!isLoggedIn || !isBusiness)) {
                    return false;
                }
                
                // 로그인 상태별 메뉴 필터링
                if (!menu.is_business_only) {
                    if (!menu.is_logged && !menu.is_not_logged) {
                        return true;
                    }
                    if (menu.is_logged && !isLoggedIn) {
                        return false;
                    }
                    if (menu.is_not_logged && isLoggedIn) {
                        return false;
                    }
                }
                
                return true;
            });
            
            return {
                label: rootMenu.name || rootMenu.title || rootMenu.menu_name || '메뉴',
                to: getUrl(rootMenu),
                externalLink: rootMenu.externalLink,
                children: children.map(child => {
                    // 자식의 자식(손자) 찾기 (로그인 상태 필터링 적용)
                    const grandChildren = sortedMenus.filter(menu => {
                        if (menu.parentId !== child.id) return false;
                        if (menu.visible === false) return false;
                        
                        // 사업자 전용 메뉴 필터링
                        if (menu.is_business_only && (!isLoggedIn || !isBusiness)) {
                            return false;
                        }
                        
                        // 로그인 상태별 메뉴 필터링
                        if (!menu.is_business_only) {
                            if (!menu.is_logged && !menu.is_not_logged) {
                                return true;
                            }
                            if (menu.is_logged && !isLoggedIn) {
                                return false;
                            }
                            if (menu.is_not_logged && isLoggedIn) {
                                return false;
                            }
                        }
                        
                        return true;
                    });
                    
                    return {
                        label: child.name || child.title || child.menu_name || '서브메뉴',
                        to: getUrl(child),
                        children: grandChildren.map(grandChild => ({
                            label: grandChild.name || grandChild.title || grandChild.menu_name || '서브메뉴',
                            to: getUrl(grandChild)
                        }))
                    };
                })
            };
        });
    })() : GNB_MOCK_DATA;
    

    return (
        <ul
            className="desktopGnb lg:flex hidden items-center justify-start gap-x-7 opacity-0"
            onMouseEnter={handleGnbMouseEnter}
            onMouseLeave={handleGnbMouseLeave}
        >
            {navigationData.map((item, index) => (
                <li
                    key={`menu-${index}-${item.label}`}
                    className="relative py-4 group"
                    onMouseEnter={() => handleMouseHover(item)}
                >
                    <a
                        href={item.to}
                        className="text-sm leading-logo hover-primary whitespace-nowrap"
                        target={item.externalLink ? "_blank" : undefined}
                        rel={item.externalLink ? "noopener noreferrer" : undefined}
                    >
                        {item.label}
                    </a>
                </li>
            ))}
        </ul>
    );
}

// 데스크톱 GNB 메가메뉴 컴포넌트
function DesktopGnbMegaMenu({ hoveredItem, handleMegaMenuMouseEnter, handleMegaMenuMouseLeave, isOpen }: { hoveredItem: GNBData | null; handleMegaMenuMouseEnter: () => void; handleMegaMenuMouseLeave: () => void; isOpen: boolean }) {

    return (
        <div
            className={`megaMenu lg:flex absolute w-full h-auto items-center justify-center border-t border-black/10 bg-white/95 transition-all duration-300 ease-in-out ${
                isOpen && hoveredItem && hoveredItem.children && hoveredItem.children.length > 0
                    ? 'opacity-100 visible'
                    : 'opacity-0 invisible hidden'
            }`}
            style={{ 
                zIndex: 9999,
                pointerEvents: isOpen && hoveredItem && hoveredItem.children && hoveredItem.children.length > 0 ? 'auto' : 'none'
            }}
            onMouseEnter={handleMegaMenuMouseEnter}
            onMouseLeave={handleMegaMenuMouseLeave}
        >
            {hoveredItem && hoveredItem.children && (
                <div className="globalWrapper w-full py-10">
                    <div className="flex justify-start gap-x-20">
                        {hoveredItem.children.map((category, index) => (
                            <DesktopGnbMegaMenuCategory
                                key={`category-${index}-${category.label}`}
                                category={category}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// 데스크톱 GNB 메가메뉴 카테고리 컴포넌트
function DesktopGnbMegaMenuCategory({ category }: { category: GNBData }) {
    return (
        <div className="flex-shrink-0 mr-12">
            {/* 카테고리 제목 */}
            <div className="mb-4">
                <h4>
                    <a
                        href={category.to}
                        className="text-sm hover-primary block leading-logo whitespace-nowrap"
                    >
                        {category.label}
                    </a>
                </h4>
            </div>

            {/* 서브 카테고리 목록 */}
            {category.children && category.children.length > 0 && (
                <ul className="space-y-2.5">
                    {category.children.map((subItem, subIndex) => (
                        <li key={`subitem-${subIndex}-${subItem.label}`}>
                            <a
                                href={subItem.to}
                                className="text-sm text-black/60 hover-black block leading-logo whitespace-nowrap"
                            >
                                {subItem.label}
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function MobileGnb({ onOpen }: { onOpen: () => void }) {
    // 모바일에서는 애니메이션 없이 바로 표시
    useEffect(() => {
        const gnb = document.querySelector('.mobileGnb');
        if (gnb) {
            (gnb as HTMLElement).style.opacity = '1';
        }
    }, []);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen();
    };

    return (
        <div className="mobileGnb lg:hidden relative z-50" style={{ opacity: 1 }}>
            <button
                className="block px-3 py-3.5 md:py-5 relative"
                aria-label="메뉴"
                type="button"
                onClick={handleClick}
                style={{ cursor: 'pointer', minWidth: '44px', minHeight: '44px', touchAction: 'manipulation' }}
            >
                <MenuIcon />
            </button>
        </div>
    );
}
